import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useTranslation } from 'react-i18next';
import { StationPopup } from './StationPopup';
import { AreaOverlay } from './AreaOverlay';
import { HeatmapLayer } from './HeatmapLayer';
import { HeatmapLegend } from './HeatmapLegend';
import { WindVectorField } from './WindVectorField';
import { WindDifferentialField } from './WindDifferentialField';
import { HeatmapAlert } from './HeatmapAlert';
import { groupStationsByArea } from '../../utils/areaOverlayUtils';
import './LeafletMap.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ============================================================================
// CONSTANTS
// ============================================================================
const MAP_CONFIG = {
  MIN_ZOOM_FOR_MARKERS: 17,  // Zoom level where clusters are disabled
  POPUP_PADDING: 80,         // Padding around popup when auto-panning
  PAN_DURATION: 0.6,         // Shorter pan for snappier feel
};

// Create custom marker with station code
const createCustomIcon = (stationCode, status, sensorType) => {
  const colors = {
    active: '#5A7A3A',
    warning: '#8A7A4A',
    inactive: '#8A4A3A'
  };
  
  const bgColor = colors[status] || colors.active;
  const pulse = status === 'active' ? 'marker-pulse' : status === 'warning' ? 'marker-pulse-fast' : '';
  
  // Different station icons for different types
  let iconSvg;
  if (sensorType === 'retro_modulo') {
    // Retro Modulo station icon - solar panel integrated station
    iconSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none">
        <!-- Station base/pole -->
        <line x1="12" y1="19" x2="12" y2="22" stroke="${bgColor}" stroke-width="2.5" stroke-linecap="round"></line>
        <!-- Solar panel on top -->
        <rect x="5" y="4" width="14" height="8" rx="1" fill="none" stroke="${bgColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></rect>
        <line x1="5" y1="7" x2="19" y2="7" stroke="${bgColor}" stroke-width="1.5" stroke-linecap="round"></line>
        <line x1="5" y1="10" x2="19" y2="10" stroke="${bgColor}" stroke-width="1.5" stroke-linecap="round"></line>
        <line x1="9" y1="4" x2="9" y2="12" stroke="${bgColor}" stroke-width="1.5" stroke-linecap="round"></line>
        <line x1="15" y1="4" x2="15" y2="12" stroke="${bgColor}" stroke-width="1.5" stroke-linecap="round"></line>
        <!-- Station housing below panel -->
        <rect x="8" y="12" width="8" height="6" rx="1" fill="none" stroke="${bgColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></rect>
        <!-- Sensor indicator -->
        <circle cx="12" cy="15" r="1.5" fill="${bgColor}" opacity="0.6"></circle>
      </svg>
    `;
  } else {
    // Campo Aperto station icon - field monitoring station
    iconSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none">
        <!-- Station base/pole -->
        <line x1="12" y1="19" x2="12" y2="22" stroke="${bgColor}" stroke-width="2.5" stroke-linecap="round"></line>
        <!-- Station housing -->
        <rect x="7" y="11" width="10" height="7" rx="1.5" fill="none" stroke="${bgColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></rect>
        <!-- Weather sensor on top -->
        <circle cx="12" cy="8" r="2.5" fill="none" stroke="${bgColor}" stroke-width="2"></circle>
        <line x1="12" y1="10.5" x2="12" y2="11" stroke="${bgColor}" stroke-width="2" stroke-linecap="round"></line>
        <!-- Wind vane -->
        <path d="M12 8 L14 6 M12 8 L10 6" stroke="${bgColor}" stroke-width="2" stroke-linecap="round"></path>
        <!-- Signal waves -->
        <path d="M8 8 Q12 4 16 8" stroke="${bgColor}" stroke-width="1.5" fill="none" stroke-linecap="round" opacity="0.7"></path>
        <!-- Display/screen -->
        <rect x="9" y="13" width="6" height="3" rx="0.5" fill="${bgColor}" opacity="0.2"></rect>
        <line x1="10" y1="14.5" x2="14" y2="14.5" stroke="${bgColor}" stroke-width="1" stroke-linecap="round"></line>
      </svg>
    `;
  }
  
  return L.divIcon({
    html: `
      <div class="custom-marker-wrapper ${pulse}">
        <div class="custom-marker-icon" style="border-color: ${bgColor};">
          ${iconSvg}
        </div>
        <div class="custom-marker-label" style="background-color: ${bgColor};">
          ${stationCode}
        </div>
      </div>
    `,
    className: 'custom-marker-container',
    iconSize: [80, 48],
    iconAnchor: [40, 44],
    popupAnchor: [0, -44]
  });
};

// Safe invalidateSize helper
const safeInvalidateSize = (map) => {
  if (!map) {
    return;
  }
  
  // Check if map is initialized and has a container
  if (!map.getContainer) {
    return;
  }
  
  try {
    const container = map.getContainer();
    if (!container) {
      return;
    }
    
    // Check if container is in the DOM
    if (!container.parentElement && !container.isConnected) {
      return;
    }
    
    // Check if map pane exists (Leaflet internal structure)
    if (!map._panes || !map._panes.mapPane) {
      return;
    }
    
    // Only call invalidateSize if all checks pass
    map.invalidateSize();
  } catch (error) {
    // Silently ignore errors during initialization or when map is being destroyed
    console.debug('Map invalidateSize called before initialization or after unmount:', error);
  }
};

// Component to handle map resizing when panel opens/closes
function MapResizer({ panelOpen }) {
  const map = useMap();
  
  useEffect(() => {
    // Invalidate size once after layout settles
    const invalidateSize = () => {
      requestAnimationFrame(() => safeInvalidateSize(map));
      setTimeout(() => safeInvalidateSize(map), 120);
    };

    invalidateSize();
  }, [map, panelOpen]);
  
  return null;
}

// Component to handle WMS tile layer for cadastral map
// Note: The Italian WMS service has CORS restrictions, so tiles won't load directly
// A fallback base map is provided so the map remains usable
function WMSTileLayerComponent({ url, layers, format, transparent, attribution, maxZoom, opacity }) {
  const map = useMap();
  const layerRef = useRef(null);
  
  useEffect(() => {
    // Remove existing layer if any
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }
    
    try {
      // Create WMS layer (will attempt to load but may fail due to CORS)
      const wmsOptions = {
        layers: layers,
        format: format || 'image/png',
        transparent: transparent !== false,
        attribution: attribution,
        maxZoom: maxZoom || 19,
        version: '1.1.0',
        uppercase: true,
        styles: '',
        crossOrigin: 'anonymous'
      };
      
      if (opacity !== undefined) {
        wmsOptions.opacity = opacity;
      }
      
      const wmsLayer = L.tileLayer.wms(url, wmsOptions);
      
      // Silently handle errors (CORS is expected)
      wmsLayer.on('tileerror', () => {
        // Errors are expected due to CORS - fallback map will show
      });
      
      wmsLayer.addTo(map);
      layerRef.current = wmsLayer;
      
    } catch (error) {
      console.error('Error creating WMS layer:', error);
    }
    
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, url, layers, format, transparent, attribution, maxZoom, opacity]);
  
  return null;
}

// Component to handle cluster click events
function ClusterClickHandler({ clusterGroupRef }) {
  const map = useMap();
  const handlerRef = useRef(null);
  const attachedRef = useRef(null);
  
  useEffect(() => {
    if (!map) return;
    
    // Create handler once
    if (!handlerRef.current) {
      handlerRef.current = (e) => {
        try {
          if (!map || !map.getContainer()) return;
          const cluster = e.layer;
          if (cluster && cluster.getBounds) {
            const bounds = cluster.getBounds();
            map.fitBounds(bounds, {
              padding: [50, 50],
              maxZoom: 19,
              animate: false
            });
          }
        } catch (error) {
          console.warn('Error fitting cluster bounds:', error);
        }
      };
    }
    
    const attachHandler = () => {
      let clusterGroup = null;
      
      // Try ref first
      if (clusterGroupRef?.current) {
        const refCluster = clusterGroupRef.current.leafletElement || clusterGroupRef.current;
        if (refCluster?._featureGroup) {
          clusterGroup = refCluster;
        }
      }
      
      // Search map layers
      if (!clusterGroup) {
        map.eachLayer((layer) => {
          if (layer._featureGroup && layer._nonPointGroup && typeof layer.on === 'function') {
            clusterGroup = layer;
            return;
          }
        });
      }
      
      // Attach if found and different
      if (clusterGroup && clusterGroup !== attachedRef.current) {
        if (attachedRef.current && handlerRef.current) {
          try {
            attachedRef.current.off('clusterclick', handlerRef.current);
          } catch (e) {}
        }
        
        if (handlerRef.current) {
          try {
            clusterGroup.on('clusterclick', handlerRef.current);
            attachedRef.current = clusterGroup;
          } catch (e) {
            console.warn('Failed to attach cluster click handler:', e);
          }
        }
      }
    };
    
    // Listen for layer additions
    const handleLayerAdd = () => {
      setTimeout(attachHandler, 50);
    };
    
    map.on('layeradd', handleLayerAdd);
    
    // Try multiple times
    attachHandler();
    const timeouts = [
      setTimeout(attachHandler, 100),
      setTimeout(attachHandler, 300),
      setTimeout(attachHandler, 600),
      setTimeout(attachHandler, 1000)
    ];
    
    return () => {
      map.off('layeradd', handleLayerAdd);
      timeouts.forEach(clearTimeout);
      
      if (attachedRef.current && handlerRef.current) {
        try {
          attachedRef.current.off('clusterclick', handlerRef.current);
        } catch (e) {}
        attachedRef.current = null;
      }
    };
  }, [map, clusterGroupRef]);
  
  return null;
}

// Component to fit map bounds to stations and polygons (same as CompactMapView)
function FitBoundsToStations({ stations, farm }) {
  const map = useMap();
  
  useEffect(() => {
    if (!farm) return;
    const baseStations = farm.stations || stations;
    if (!baseStations || baseStations.length === 0) return;
    if (!map) return;
    
    // Check if map is properly initialized
    const container = map.getContainer();
    if (!container) return;
    
    // Use a small delay to ensure map is fully initialized
    const timeoutId = setTimeout(() => {
      try {
        // Verify map is still valid
        if (!map || !map.getContainer()) return;
        
        // Get bounds from both station positions and polygon areas
        const bounds = L.latLngBounds();
        
        // Add station positions
        baseStations.forEach(station => {
          if (station.locationLat != null && station.locationLon != null) {
            bounds.extend([station.locationLat, station.locationLon]);
          }
        });
        
        if (farm?.sections && farm.sections.length > 0) {
          farm.sections.forEach(section => {
            if (section.polygon && section.polygon.length > 0) {
              section.polygon.forEach(([lat, lon]) => {
                bounds.extend([lat, lon]);
              });
            }
          });
        } else {
          // Add polygon bounds from stations (hectare areas)
          const areaGroups = groupStationsByArea(baseStations);
          Object.values(areaGroups).forEach(area => {
            if (area.bounds) {
              bounds.extend([area.bounds.minLat, area.bounds.minLon]);
              bounds.extend([area.bounds.minLat, area.bounds.maxLon]);
              bounds.extend([area.bounds.maxLat, area.bounds.minLon]);
              bounds.extend([area.bounds.maxLat, area.bounds.maxLon]);
            }
          });
        }

        if (bounds.isValid()) {
          // Calculate optimal padding based on map container size
          const containerWidth = container.offsetWidth || 800;
          const containerHeight = container.offsetHeight || 400;
          
          // Use absolute minimal padding for maximum zoom while still showing all stations
          // Padding as percentage of container size (same as CompactMapView)
          const paddingX = Math.max(10, containerWidth * 0.015); // Absolute minimal padding: 10px or 1.5% of width
          const paddingY = Math.max(10, containerHeight * 0.015); // Absolute minimal padding: 10px or 1.5% of height
          
          // Fit bounds with maximum zoom level (no limit) - same as CompactMapView
          map.fitBounds(bounds, {
            maxZoom: null, // No max zoom limit - allow maximum possible zoom
            padding: [paddingY, paddingX], // Absolute minimal padding for maximum zoom
            animate: false, // Disable animation to prevent transition errors
          });
        }
      } catch (error) {
        console.warn('Error fitting map bounds:', error);
      }
    }, 120);
    
    return () => clearTimeout(timeoutId);
  }, [map, farm?.id]);
  
  return null;
}

// Component to handle map updates and events
function MapEventHandler({ farm }) {
  const map = useMap();
  const isDraggingRef = useRef(false);
  
  // Close popups when user drags/moves the map (not programmatic moves)
  useEffect(() => {
    const handleDragStart = () => {
      isDraggingRef.current = true;
      map.closePopup();
    };
    
    const handleDragEnd = () => {
      isDraggingRef.current = false;
    };
    
    const handleZoomStart = () => {
      // Close popup on manual zoom (scroll wheel, buttons)
      map.closePopup();
    };
    
    map.on('dragstart', handleDragStart);
    map.on('dragend', handleDragEnd);
    map.on('zoomstart', handleZoomStart);
    
    return () => {
      map.off('dragstart', handleDragStart);
      map.off('dragend', handleDragEnd);
      map.off('zoomstart', handleZoomStart);
    };
  }, [map]);
  
  // Dispatch map move events for auto-collapse
  useEffect(() => {
    const handleMove = () => {
      window.dispatchEvent(new CustomEvent('map:move'));
    };
    
    map.on('move', handleMove);
    map.on('moveend', handleMove);
    
    return () => {
      map.off('move', handleMove);
      map.off('moveend', handleMove);
    };
  }, [map]);
  
  // Handle sidebar toggle and invalidate size events
  useEffect(() => {
    const invalidateMap = () => {
      requestAnimationFrame(() => {
        safeInvalidateSize(map);
        setTimeout(() => safeInvalidateSize(map), 150);
      });
    };
    
    window.addEventListener('sidebar:toggled', invalidateMap);
    window.addEventListener('map:invalidate-size', invalidateMap);
    return () => {
      window.removeEventListener('sidebar:toggled', invalidateMap);
      window.removeEventListener('map:invalidate-size', invalidateMap);
    };
  }, [map]);
  
  return null;
}

// ============================================================================
// STATION FOCUS CONTROLLER - Clean, simple station navigation
// ============================================================================
function StationFocusController({ markerRefs }) {
  const map = useMap();
  const pendingPopupRef = useRef(null);
  
  useEffect(() => {
    const handleCenterStation = (e) => {
      const { lat, lon } = e.detail;
      if (!lat || !lon || !map) return;
      
      // Cancel any pending popup opening
      if (pendingPopupRef.current) {
        clearTimeout(pendingPopupRef.current);
        pendingPopupRef.current = null;
      }
      
      const currentZoom = map.getZoom();
      const targetLatLng = L.latLng(lat, lon);
      
      // Step 1: Ensure we're at a zoom level where individual markers are visible
      if (currentZoom < MAP_CONFIG.MIN_ZOOM_FOR_MARKERS) {
        map.setZoom(MAP_CONFIG.MIN_ZOOM_FOR_MARKERS, { animate: false });
      }
      
      // Step 2: Pan smoothly to the station
      map.panTo(targetLatLng, {
        duration: MAP_CONFIG.PAN_DURATION,
        easeLinearity: 0.25
      });
      
      // Step 3: Open popup after pan completes (only if not already open)
      const openPopupDelay = MAP_CONFIG.PAN_DURATION * 1000 + 100;
      
      pendingPopupRef.current = setTimeout(() => {
        pendingPopupRef.current = null;
        
        // Check if there's already an open popup at this location
        const currentPopup = map._popup;
        if (currentPopup && currentPopup.isOpen()) {
          const popupLatLng = currentPopup.getLatLng();
          if (popupLatLng && 
              Math.abs(popupLatLng.lat - lat) < 0.0001 && 
              Math.abs(popupLatLng.lng - lon) < 0.0001) {
            // Popup already open at this location, don't reopen
            return;
          }
        }
        
        // Find and open the marker popup
        const markerKey = `${lat.toFixed(6)},${lon.toFixed(6)}`;
        const marker = markerRefs.current?.get(markerKey);
        
        if (marker) {
          marker.openPopup();
          return;
        }
        
        // Fallback: search through map layers
        map.eachLayer((layer) => {
          if (layer instanceof L.Marker) {
            const pos = layer.getLatLng();
            if (Math.abs(pos.lat - lat) < 0.0001 && Math.abs(pos.lng - lon) < 0.0001) {
              layer.openPopup();
            }
          }
          if (layer.eachLayer) {
            layer.eachLayer((sublayer) => {
              if (sublayer instanceof L.Marker) {
                const pos = sublayer.getLatLng();
                if (Math.abs(pos.lat - lat) < 0.0001 && Math.abs(pos.lng - lon) < 0.0001) {
                  sublayer.openPopup();
                }
              }
            });
          }
        });
      }, openPopupDelay);
    };
    
    // Cancel pending popup if user closes popup manually or moves map
    const handlePopupClose = () => {
      if (pendingPopupRef.current) {
        clearTimeout(pendingPopupRef.current);
        pendingPopupRef.current = null;
      }
    };
    
    map.on('popupclose', handlePopupClose);
    window.addEventListener('map:center-station', handleCenterStation);
    
    return () => {
      map.off('popupclose', handlePopupClose);
      window.removeEventListener('map:center-station', handleCenterStation);
      if (pendingPopupRef.current) {
        clearTimeout(pendingPopupRef.current);
      }
    };
  }, [map, markerRefs]);
  
  return null;
}

export function LeafletMap({ farm, stations, selectedStation, onStationClick, panelOpen }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const markerRefs = useRef(new Map()); // Map of "lat,lon" -> marker ref for quick lookup
  const previousStationsRef = useRef(new Map());
  const [mapLayer, setMapLayer] = useState('satellite');
  const [showAreaOverlay, setShowAreaOverlay] = useState(false);
  const [activeHeatmap, setActiveHeatmap] = useState(null);
  const [showWindVectors, setShowWindVectors] = useState(false);
  const [heatmapValues, setHeatmapValues] = useState({ min: 0, max: 0 });
  const [showHeatmapAlert, setShowHeatmapAlert] = useState(false);
  const [heatmapDropdownOpen, setHeatmapDropdownOpen] = useState(false);
  const [tilesLoading, setTilesLoading] = useState(false);
  const [heatmapCalculating, setHeatmapCalculating] = useState(false);
  
  // Helper to register marker refs for quick lookup
  const registerMarkerRef = useCallback((lat, lon, marker) => {
    if (marker) {
      const key = `${lat.toFixed(6)},${lon.toFixed(6)}`;
      markerRefs.current.set(key, marker);
    }
  }, []);
  
  // Listen for layer change events
  useEffect(() => {
    const handleLayerChange = (e) => {
      setMapLayer(e.detail.layerId);
    };
    
    window.addEventListener('map:change-layer', handleLayerChange);
    return () => window.removeEventListener('map:change-layer', handleLayerChange);
  }, []);
  
  // Listen for area overlay toggle
  useEffect(() => {
    const handleToggleAreaOverlay = (e) => {
      setShowAreaOverlay(e.detail.visible);
    };
    
    window.addEventListener('map:toggle-area-overlay', handleToggleAreaOverlay);
    return () => window.removeEventListener('map:toggle-area-overlay', handleToggleAreaOverlay);
  }, []);
  
  // Listen for heatmap toggle
  useEffect(() => {
    const handleToggleHeatmap = (e) => {
      const { heatmapType } = e.detail;
      // If clicking the same heatmap type, toggle it off
      setActiveHeatmap(prev => {
        const newHeatmap = prev === heatmapType ? null : heatmapType;
        // Reset values when switching heatmaps
        if (newHeatmap === null) {
          setHeatmapValues({ min: 0, max: 0 });
        }
        return newHeatmap;
      });
    };
    
    window.addEventListener('map:toggle-heatmap', handleToggleHeatmap);
    return () => window.removeEventListener('map:toggle-heatmap', handleToggleHeatmap);
  }, []);

  // Listen for heatmap dropdown open event to close popups and show alert
  // No automatic zoom - let users control the view themselves
  useEffect(() => {
    const handleHeatmapDropdownOpen = () => {
      // Mark dropdown as open
      setHeatmapDropdownOpen(true);
      
      // Show alert when dropdown opens
      setShowHeatmapAlert(true);
      
      // Close the active popup without walking every layer
      mapRef.current?.closePopup();
    };
    
    const handleHeatmapDropdownClose = () => {
      setHeatmapDropdownOpen(false);
    };
    
    window.addEventListener('map:heatmap-dropdown-open', handleHeatmapDropdownOpen);
    window.addEventListener('map:heatmap-dropdown-close', handleHeatmapDropdownClose);
    return () => {
      window.removeEventListener('map:heatmap-dropdown-open', handleHeatmapDropdownOpen);
      window.removeEventListener('map:heatmap-dropdown-close', handleHeatmapDropdownClose);
    };
  }, [stations]);

  // Show/hide alert based on heatmap state and ensure markers are visible
  useEffect(() => {
    if (activeHeatmap) {
      setShowHeatmapAlert(true);
      // Close all popups when heatmap becomes active
      if (mapRef.current) {
        mapRef.current.closePopup();
        
        // Ensure marker pane is above overlay pane
        const markerPane = mapRef.current.getPane('markerPane');
        const overlayPane = mapRef.current.getPane('overlayPane');
        if (markerPane) {
          markerPane.style.zIndex = '600';
        }
        if (overlayPane) {
          overlayPane.style.zIndex = '200';
        }
        
        // Force marker pane to be visible
        if (markerPane) {
          markerPane.style.display = 'block';
          markerPane.style.visibility = 'visible';
        }
      }
    } else {
      setShowHeatmapAlert(false);
    }
  }, [activeHeatmap]);

  // Animate markers on data updates (debounced to prevent jitter)
  useEffect(() => {
    if (!mapRef.current || !stations || stations.length === 0) return;

    // Debounce updates to prevent jittery behavior
    const updateTimeout = setTimeout(() => {
      stations.forEach((station) => {
        const prevStation = previousStationsRef.current.get(station.id);
        
        // Only update if status actually changed (not just data values)
        if (prevStation && prevStation.status !== station.status) {
          // Find the marker and trigger update animation
          // Use a more stable approach - iterate through cluster groups
          mapRef.current.eachLayer((layer) => {
            // Check if it's a cluster group
            if (layer.eachLayer) {
              layer.eachLayer((sublayer) => {
                if (sublayer instanceof L.Marker) {
                  const markerLat = sublayer.getLatLng().lat;
                  const markerLon = sublayer.getLatLng().lng;
                  if (Math.abs(markerLat - station.locationLat) < 0.0001 && 
                      Math.abs(markerLon - station.locationLon) < 0.0001) {
                    const element = sublayer.getElement();
                    if (element) {
                      const wrapper = element.querySelector('.custom-marker-wrapper');
                      if (wrapper && !wrapper.classList.contains('marker-update-animation')) {
                        // Add update animation class
                        wrapper.classList.add('marker-update-animation');
                        // Update icon with new status
                        const newIcon = createCustomIcon(station.stationCode, station.status, station.sensorType);
                        sublayer.setIcon(newIcon);
                        // Remove animation class after animation completes
                        setTimeout(() => {
                          if (wrapper) {
                            wrapper.classList.remove('marker-update-animation');
                          }
                        }, 600);
                      }
                    }
                  }
                }
              });
            } else if (layer instanceof L.Marker) {
              // Handle standalone markers
              const markerLat = layer.getLatLng().lat;
              const markerLon = layer.getLatLng().lng;
              if (Math.abs(markerLat - station.locationLat) < 0.0001 && 
                  Math.abs(markerLon - station.locationLon) < 0.0001) {
                const element = layer.getElement();
                if (element) {
                  const wrapper = element.querySelector('.custom-marker-wrapper');
                  if (wrapper && !wrapper.classList.contains('marker-update-animation')) {
                    wrapper.classList.add('marker-update-animation');
                    const newIcon = createCustomIcon(station.stationCode, station.status, station.sensorType);
                    layer.setIcon(newIcon);
                    setTimeout(() => {
                      if (wrapper) {
                        wrapper.classList.remove('marker-update-animation');
                      }
                    }, 600);
                  }
                }
              }
            }
          });
        }
        
        // Update previous station data
        previousStationsRef.current.set(station.id, {
          status: station.status,
          temperature: station.temperature,
          humidity: station.humidity,
          windSpeed: station.windSpeed
        });
      });
    }, 300); // Debounce for 300ms

    return () => clearTimeout(updateTimeout);
  }, [stations]);



  // Ensure markers are always visible above heatmap layers
  useEffect(() => {
    if (!mapRef.current) return;
    
    const ensureMarkersVisible = () => {
      const map = mapRef.current;
      if (!map) return;
      
      const markerPane = map.getPane('markerPane');
      const overlayPane = map.getPane('overlayPane');
      
      if (markerPane) {
        markerPane.style.zIndex = '600';
      }
      if (overlayPane) {
        overlayPane.style.zIndex = '200';
      }
    };
    
    ensureMarkersVisible();
    mapRef.current.on('zoomend', ensureMarkersVisible);
    
    return () => {
      mapRef.current?.off('zoomend', ensureMarkersVisible);
    };
  }, [activeHeatmap]);
  
  // Listen for wind vector field toggle
  useEffect(() => {
    const handleToggleWindVectors = (e) => {
      setShowWindVectors(e.detail.visible);
    };
    
    window.addEventListener('map:toggle-wind-vectors', handleToggleWindVectors);
    return () => window.removeEventListener('map:toggle-wind-vectors', handleToggleWindVectors);
  }, []);
  
  if (!farm || !farm.centerCoordinates) {
    return <div className="map-loading">{t('map.loading')}</div>;
  }
  
  const center = [farm.centerCoordinates.lat, farm.centerCoordinates.lon];
  
  // Render appropriate tile layer based on selection
  const renderTileLayer = () => {
    switch (mapLayer) {
      case 'road':
        return (
          <TileLayer
            key="road-layer"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
        );
      case 'satellite':
        return (
          <TileLayer
            key="satellite-layer"
            attribution='Esri, DigitalGlobe, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
        );
      case 'cadastral':
        // Cadastral map - Mappa Catastale with fallback base map
        // Note: Official WMS service has CORS restrictions, so we add a base map fallback
        // For production: implement backend proxy to fetch WMS tiles
        return (
          <>
            {/* Fallback base map - shows when WMS tiles fail due to CORS */}
            <TileLayer
              key="cadastral-fallback-base"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
              opacity={0.3}
            />
            {/* Cadastral WMS layer - may fail due to CORS */}
            <WMSTileLayerComponent
              key="cadastral-wms-layer"
              url="https://wms.cartografia.agenziaentrate.gov.it/inspire/wms/ows01.php"
              layers="CP.CadastralParcel"
              format="image/png"
              transparent={false}
              attribution='© Agenzia delle Entrate - Mappa Catastale'
              maxZoom={19}
              opacity={1.0}
            />
          </>
        );
      default:
        return (
          <TileLayer
            key="default-layer"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
        );
    }
  };
  
  return (
    <div className={`leaflet-map-container ${panelOpen ? 'leaflet-map-container--panel-open' : ''}`}>
      {/* Heatmap Alert */}
      <HeatmapAlert 
        visible={showHeatmapAlert}
        onClose={() => setShowHeatmapAlert(false)}
      />
      
      <MapContainer
        center={center}
        zoom={15}
        scrollWheelZoom={true}
        zoomControl={false}
        className="leaflet-map"
        ref={mapRef}
        whenCreated={(map) => {
          // Ensure marker pane is always above overlay pane
          const markerPane = map.getPane('markerPane');
          const overlayPane = map.getPane('overlayPane');
          if (markerPane) {
            markerPane.style.zIndex = '600';
          }
          if (overlayPane) {
            overlayPane.style.zIndex = '200';
          }
          
          // Track tile loading - only show loading when tiles are actually loading
          const loadingTilesRef = { count: 0 };
          let hideTimeout = null;
          
          const updateLoadingState = () => {
            if (loadingTilesRef.count > 0) {
              setTilesLoading(true);
              // Clear any pending hide timeout
              if (hideTimeout) {
                clearTimeout(hideTimeout);
                hideTimeout = null;
              }
            } else {
              // Hide loading after a short delay to ensure all tiles are loaded
              if (hideTimeout) {
                clearTimeout(hideTimeout);
              }
              hideTimeout = setTimeout(() => {
                setTilesLoading(false);
                hideTimeout = null;
              }, 300);
            }
          };
          
          const handleTileLoadStart = () => {
            loadingTilesRef.count++;
            updateLoadingState();
          };
          
          const handleTileLoad = () => {
            loadingTilesRef.count = Math.max(0, loadingTilesRef.count - 1);
            updateLoadingState();
          };
          
          const handleTileError = () => {
            loadingTilesRef.count = Math.max(0, loadingTilesRef.count - 1);
            updateLoadingState();
          };
          
          // Listen to tile layer events
          map.on('tileloadstart', handleTileLoadStart);
          map.on('tileload', handleTileLoad);
          map.on('tileerror', handleTileError);
          
          // Also listen to move/zoom events to track new tile loading
          const handleMoveStart = () => {
            // When map starts moving, tiles will start loading
            // The tileloadstart events will handle setting loading state
          };
          
          map.on('movestart', handleMoveStart);
          map.on('zoomstart', handleMoveStart);
          
          // When map is ready, check if we need to hide loading
          map.whenReady(() => {
            setTimeout(() => {
              if (loadingTilesRef.count === 0) {
                setTilesLoading(false);
              }
            }, 500);
          });
        }}
      >
        {/* Tile Layers */}
        {renderTileLayer()}
        
        {/* Map Resizer - handles panel open/close */}
        <MapResizer panelOpen={panelOpen} />
        
        {/* Map Event Handler */}
        <MapEventHandler farm={farm} />
        
        {/* Station Focus Controller - handles clean station navigation */}
        <StationFocusController markerRefs={markerRefs} />
        
        {/* Fit Bounds to Stations - same zoom calculation as analytics page */}
        <FitBoundsToStations stations={stations} farm={farm} />
        
        {/* Cluster Click Handler */}
        <ClusterClickHandler clusterGroupRef={clusterGroupRef} />
        
        {/* Area Overlay - shows field groups (Section A / B) */}
        <AreaOverlay farm={farm} stations={stations} visible={showAreaOverlay} enableTooltips={true} />
        
        {/* Heatmap Layers */}
        <HeatmapLayer 
          stations={stations} 
          dataType="temperature" 
          visible={activeHeatmap === 'temperature'}
          onValuesChange={useCallback((values) => {
            if (activeHeatmap === 'temperature') {
              setHeatmapValues(values);
            }
          }, [activeHeatmap])}
          onCalculatingChange={setHeatmapCalculating}
        />
        <HeatmapLayer 
          stations={stations} 
          dataType="windSpeed" 
          visible={activeHeatmap === 'windSpeed'}
          onValuesChange={useCallback((values) => {
            if (activeHeatmap === 'windSpeed') {
              setHeatmapValues(values);
            }
          }, [activeHeatmap])}
          onCalculatingChange={setHeatmapCalculating}
        />
        
        {/* Wind Differential Field - shows many small arrows across the field showing wind flow pattern */}
        {/* Automatically shown when wind speed heatmap is active */}
        <WindDifferentialField 
          stations={stations} 
          visible={activeHeatmap === 'windSpeed'} 
          farm={farm}
        />
        
        {/* Heatmap Legend */}
        <HeatmapLegend 
          dataType={activeHeatmap}
          minValue={heatmapValues.min}
          maxValue={heatmapValues.max}
          visible={activeHeatmap !== null}
          stations={stations}
        />
        
        {/* Wind Vector Field - shows wind direction at station locations */}
        <WindVectorField 
          stations={stations} 
          visible={showWindVectors} 
        />
        
        {/* Markers with Clustering */}
        <MarkerClusterGroup
          ref={clusterGroupRef}
          chunkedLoading
          maxClusterRadius={30}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={false}
          disableClusteringAtZoom={MAP_CONFIG.MIN_ZOOM_FOR_MARKERS}
          removeOutsideVisibleBounds={false}
          animate={false}
          iconCreateFunction={(cluster) => {
            const count = cluster.getChildCount();
            return L.divIcon({
              html: `<div class="custom-cluster-marker"><span>${count}</span></div>`,
              className: 'custom-cluster-wrapper',
              iconSize: [56, 56]
            });
          }}
        >
          {stations.map((station) => (
            <Marker
              key={station.id}
              position={[station.locationLat, station.locationLon]}
              icon={createCustomIcon(station.stationCode, station.status, station.sensorType)}
              ref={(ref) => registerMarkerRef(station.locationLat, station.locationLon, ref)}
              eventHandlers={{
                click: (e) => {
                  // Block popups when heatmap is active
                  if (activeHeatmap) {
                    e.originalEvent?.stopPropagation();
                    e.target?.closePopup?.();
                    mapRef.current?.closePopup();
                    setShowHeatmapAlert(true);
                    return;
                  }
                  
                  onStationClick(station);
                  window.dispatchEvent(new CustomEvent('map:station-clicked'));
                }
              }}
            >
              <Popup 
                autoPan={true}
                autoPanPadding={[MAP_CONFIG.POPUP_PADDING, MAP_CONFIG.POPUP_PADDING]}
                keepInView={true}
                closeOnClick={true}
                autoClose={true}
                eventHandlers={{
                  add: () => {
                    // Close popup immediately if heatmap is active
                    if (activeHeatmap) {
                      mapRef.current?.closePopup();
                    }
                  }
                }}
              >
                <StationPopup station={station} />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
        
        {/* Zoom Controls */}
        <div className="map-zoom-controls">
          <button
            className="map-zoom-controls__btn"
            onClick={() => mapRef.current?.setZoom(mapRef.current.getZoom() + 1)}
            title={t('map.zoomIn')}
          >
            +
          </button>
          <button
            className="map-zoom-controls__btn"
            onClick={() => mapRef.current?.setZoom(mapRef.current.getZoom() - 1)}
            title={t('map.zoomOut')}
          >
            −
          </button>
        </div>
        
        {/* Tile Loading Skeleton */}
        {tilesLoading && (
          <div className="map-tile-skeleton">
            <div className="map-tile-skeleton__overlay"></div>
            <div className="map-tile-skeleton__spinner">
              <div className="map-tile-skeleton__spinner-circle"></div>
            </div>
            <div className="map-tile-skeleton__text">{t('map.loadingTiles')}</div>
          </div>
        )}
        
        {/* Heatmap Calculation Progress */}
        {heatmapCalculating && activeHeatmap && (
          <div className="heatmap-progress">
            <div className="heatmap-progress__spinner">
              <div className="heatmap-progress__spinner-circle"></div>
            </div>
            <div className="heatmap-progress__text">{t('map.calculatingHeatmap')}</div>
          </div>
        )}
      </MapContainer>
    </div>
  );
}